import type { WorkflowDefinitionDraftDetail } from './platform-workflow-definition-lifecycle.models';

type PublicationDraft = Pick<
  WorkflowDefinitionDraftDetail,
  | 'revision'
  | 'validationStatusCode'
  | 'runtimeBindingCode'
  | 'isExecutable'
  | 'lastContentAuthorUserId'
  | 'requiresIndependentPublisher'
  | 'lifecycleStatusCode'
>;

export interface WorkflowPublicationContext {
  hasUnsavedChanges: boolean;
  canPublish: boolean;
  actorUserId: number | null | undefined;
  interactionBlocked: boolean;
  validationInProgress?: boolean;
  validationFailed?: boolean;
}

export interface WorkflowPublicationReadiness {
  code:
    | 'draft_required'
    | 'draft_archived'
    | 'save_required'
    | 'validation_required'
    | 'validation_invalid'
    | 'publisher_required'
    | 'identity_unavailable'
    | 'author_identity_unavailable'
    | 'independent_publisher_required'
    | 'operation_in_progress'
    | 'ready';
  status: 'attention' | 'blocked' | 'complete';
  label: string;
  detail: string;
  blockedReason: string | null;
}

export interface WorkflowDraftExecutionSupport {
  status: 'attention' | 'complete';
  label: string;
  detail: string;
}

/** Execution support is required for activation, independently of publication. */
export function workflowDraftExecutionSupport(
  draft: Pick<
    WorkflowDefinitionDraftDetail,
    'revision' | 'validationStatusCode' | 'runtimeBindingCode' | 'isExecutable'
  > | null,
): WorkflowDraftExecutionSupport {
  if (!draft)
    return {
      status: 'attention',
      label: 'Open a saved draft',
      detail: 'Open a saved draft to check activation support.',
    };
  if (draft.isExecutable)
    return {
      status: 'complete',
      label: 'Execution support available',
      detail: `Saved revision ${draft.revision} has process execution support. Publication and activation remain separate actions; activation checks are confirmed when you activate a published version.`,
    };
  if (draft.runtimeBindingCode === 'metadata_only')
    return {
      status: 'attention',
      label: 'Activation support unavailable',
      detail: `Saved revision ${draft.revision} can be published after its publication checks pass. It cannot be activated or start process runs until process execution support is available. Ask your platform administrator to check process support.`,
    };
  if (draft.validationStatusCode !== 'valid')
    return {
      status: 'attention',
      label: 'Check after validation',
      detail: `Saved revision ${draft.revision} must pass validation before its activation support can be confirmed. Publication and activation remain separate actions.`,
    };
  return {
    status: 'attention',
    label: 'Activation support not confirmed',
    detail: `Saved revision ${draft.revision} passed validation and can be published after its publication checks pass. Publication checks run automatically before publishing. Ask your platform administrator to check process execution support before activating a published version.`,
  };
}

/** Publication prerequisites for the exact saved revision. Server authorization remains authoritative. */
export function workflowDraftPublicationReadiness(
  draft: PublicationDraft | null,
  context: WorkflowPublicationContext,
): WorkflowPublicationReadiness {
  const blocked = (
    code: WorkflowPublicationReadiness['code'],
    label: string,
    detail: string,
    status: WorkflowPublicationReadiness['status'] = 'blocked',
  ): WorkflowPublicationReadiness => ({
    code,
    status,
    label,
    detail,
    blockedReason: detail,
  });

  if (!draft)
    return blocked(
      'draft_required',
      'Open a saved draft',
      'Open a saved draft before publishing.',
    );
  if (draft.lifecycleStatusCode === 'archived')
    return blocked(
      'draft_archived',
      'Archived draft',
      'This draft has already been published and is read only. Create or continue a draft from the published version.',
    );
  if (context.hasUnsavedChanges)
    return blocked(
      'save_required',
      'Save changes first',
      'Save all local changes before publishing this revision.',
      'attention',
    );
  if (context.validationInProgress)
    return blocked(
      'operation_in_progress',
      'Checking saved revision',
      `Saved revision ${draft.revision} is being checked automatically. Publication will become available when its checks pass.`,
      'attention',
    );
  if (context.validationFailed)
    return blocked(
      'validation_required',
      'Publication checks could not finish',
      `The checks for saved revision ${draft.revision} could not finish. Review the error and retry the checks before publishing.`,
      'attention',
    );
  if (draft.validationStatusCode === 'invalid')
    return blocked(
      'validation_invalid',
      'Resolve validation findings',
      `Saved revision ${draft.revision} must pass validation before publishing.`,
    );
  if (draft.validationStatusCode !== 'valid')
    return blocked(
      'validation_required',
      'Checking publication readiness',
      `Saved revision ${draft.revision} must pass validation before publishing. Checks run automatically when Review & publish opens.`,
      'attention',
    );
  if (!context.canPublish)
    return blocked(
      'publisher_required',
      'Publisher required',
      'An authorized workflow publisher must review and publish this revision.',
      'attention',
    );
  if (!context.actorUserId)
    return blocked(
      'identity_unavailable',
      'Publisher identity unavailable',
      'Your signed-in identity must be available before you can publish this revision.',
      'attention',
    );
  if (!draft.lastContentAuthorUserId)
    return blocked(
      'author_identity_unavailable',
      'Saved author information unavailable',
      'Reload this draft before publishing. Its last saved author must be recorded.',
      'attention',
    );
  if (
    draft.requiresIndependentPublisher !== false &&
    draft.lastContentAuthorUserId === context.actorUserId
  )
    return blocked(
      'independent_publisher_required',
      'Another person must publish this revision',
      'This installation requires someone other than the last saved author to publish. Ask another person with permission to publish workflows to review this revision.',
      'attention',
    );
  if (context.interactionBlocked)
    return blocked(
      'operation_in_progress',
      'Operation in progress',
      'Wait for the current draft operation to finish before publishing.',
      'attention',
    );
  return {
    code: 'ready',
    status: 'complete',
    label: 'Ready for publication review',
    detail: draft.isExecutable
      ? `Saved revision ${draft.revision} passed validation and is supported for publication. Review and confirm publication; activation remains a separate action.`
      : `Saved revision ${draft.revision} passed validation and can be published. Review and confirm publication; process execution support must be available before activation.`,
    blockedReason: null,
  };
}
